/**
 * 운영팀 대화방 — 서버 함수 호출 + 순수 도우미 (2026-10-23 · 기획 docs/ops/ops-chat-room-plan.md · 마이그레이션 20261115100000)
 *
 * 🔒 규칙
 *   · 화면은 표(ops_chat_*)를 직접 읽거나 쓰지 않는다 — 표 권한이 없다. 아래 RPC 로만 다룬다.
 *   · 보내는 사람의 역할(크리에이터/운영팀)은 서버가 JWT 로 정한다. 화면은 역할을 보내지 않는다.
 *   · 첨부는 먼저 비공개 버킷 `ops-chat` 의 `<방 id>/<무작위 uuid>.<확장자>` 로 올리고, 그 경로를 ops_chat_send 에 넘긴다.
 *     서버가 「그 방 폴더 · 실제로 올라간 파일 · 올린 사람 = 나」를 확인한 뒤 메시지와 첨부를 한 번에 기록한다.
 *     파일 크기·종류는 저장소에 기록된 값을 서버가 쓴다(화면이 보낸 값은 믿지 않는다).
 *   · 열람은 서명 주소(5분)로만. 원래 파일명은 경로에 넣지 않는다.
 *   · 알림은 서버가 넣는다(app_config.ops_chat.notify — 대표 승인 전 꺼짐). 화면에서 notification 을 넣지 않는다.
 */
import { supabase } from "./supabaseClient";

export const OPS_CHAT_BUCKET = "ops-chat";
export const MAX_FILE_BYTES = 10 * 1024 * 1024;
export const MAX_FILES_PER_MESSAGE = 5;
export const MAX_BODY_CHARS = 2000;
export const SIGNED_URL_TTL_SEC = 300;
/** 대화 버튼 숫자를 다시 읽는 간격(대화창이 닫혀 있을 때) */
export const STATE_POLL_MS = 60 * 1000;
/** 대화창이 열려 있을 때 대화를 다시 읽는 간격 */
export const THREAD_POLL_MS = 5 * 1000;

/**
 * 받는 파일 — 확장자 → 저장소에 기록할 종류. 서버 버킷 allowed_mime_types 와 같은 목록이어야 한다(테스트가 대조).
 * zip·영상은 받지 않는다(결정 D2).
 */
export const ALLOWED_TYPES = Object.freeze({
  jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", webp: "image/webp", gif: "image/gif",
  heic: "image/heic", heif: "image/heif",
  pdf: "application/pdf",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", xls: "application/vnd.ms-excel", csv: "text/csv",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document", doc: "application/msword",
  hwp: "application/x-hwp", hwpx: "application/vnd.hancom.hwpx",
});

/** 브라우저가 바로 그릴 수 있는 사진(썸네일로 보여 줄 것) — heic 은 파일 카드로 */
const PREVIEW_IMAGE = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);

/** 화면 파일 선택 창의 accept 값 */
export const ACCEPT_ATTR = Object.keys(ALLOWED_TYPES).map((e) => `.${e}`).join(",");

export function extOf(fileName) {
  const m = /\.([A-Za-z0-9]{1,8})$/.exec(String(fileName || ""));
  return m ? m[1].toLowerCase() : "";
}

/** 업로드 전 확인 — 문제 없으면 null, 있으면 "too_big" | "bad_type" | "empty_file" */
export function validateFile(file) {
  if (!file) return "bad_type";
  if (!ALLOWED_TYPES[extOf(file.name)]) return "bad_type";
  if (!(file.size > 0)) return "empty_file";
  if (file.size > MAX_FILE_BYTES) return "too_big";
  return null;
}

export function contentTypeOf(file) {
  return ALLOWED_TYPES[extOf(file?.name)] || "application/octet-stream";
}

function randomUuid() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  // 오래된 브라우저 — RFC4122 v4 모양
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/** 저장 경로 — `<방 id>/<무작위 uuid>.<확장자 소문자>` (서버 ops_chat_room_of_path 와 같은 모양) */
export function buildAttachmentPath(roomId, fileName, uuid = randomUuid()) {
  return `${roomId}/${String(uuid).toLowerCase()}.${extOf(fileName)}`;
}

export function isPreviewableImage(mime) {
  return PREVIEW_IMAGE.has(String(mime || ""));
}

export function formatBytes(n) {
  const v = Number(n) || 0;
  if (v >= 1024 * 1024) return `${(v / (1024 * 1024)).toFixed(1)}MB`;
  if (v >= 1024) return `${Math.round(v / 1024)}KB`;
  return `${v}B`;
}

/** 서버 오류 → 짧은 코드 (raise exception 'room_closed' 등) */
export const ERROR_CODES = [
  "forbidden", "disabled", "empty", "too_long", "no_member", "self", "not_found", "room_closed", "rate_limited",
  "bad_reason", "bad_status", "not_staff", "bad_attachments", "too_many_attachments", "bad_attachment_path", "attachment_not_uploaded",
  "attachment_not_yours", "attachment_already_sent", "too_big", "bad_type", "empty_file",
];
export function errorCodeOf(err) {
  const msg = String(err?.message || err || "");
  return ERROR_CODES.find((c) => new RegExp(`\\b${c}\\b`).test(msg)) || "unknown";
}

/** 어드민 화면용 한국어 안내 (크리에이터 화면은 번역 사전을 거친다 — OpsChatLauncher) */
export const ADMIN_ERROR_TEXT = {
  forbidden: "권한이 없어요.",
  disabled: "대화방 기능이 꺼져 있어요.",
  empty: "메시지를 입력하거나 파일을 붙여 주세요.",
  too_long: "메시지는 2,000자까지 보낼 수 있어요.",
  no_member: "보낼 수 있는 회원이 아니에요(탈퇴·플랫폼사 계정 제외).",
  self: "내 계정에는 대화방을 열 수 없어요. 테스트용 크리에이터 계정을 골라 주세요.",
  not_found: "대화방을 찾을 수 없어요.",
  room_closed: "종료된 대화방이에요.",
  rate_limited: "너무 빨리 보내고 있어요. 잠시 뒤 다시 보내 주세요.",
  bad_reason: "종료 이유를 골라 주세요.",
  bad_status: "목록을 불러오지 못했어요.",
  not_staff: "운영팀 계정만 담당자로 정할 수 있어요.",
  too_many_attachments: `파일은 한 번에 ${MAX_FILES_PER_MESSAGE}개까지 보낼 수 있어요.`,
  too_big: "파일 하나는 10MB까지 올릴 수 있어요.",
  bad_type: "사진·PDF·엑셀·CSV·워드·한글 파일만 올릴 수 있어요.",
  empty_file: "빈 파일은 올릴 수 없어요.",
  attachment_not_uploaded: "파일을 올리지 못했어요. 다시 시도해 주세요.",
  attachment_not_yours: "파일을 올리지 못했어요. 다시 시도해 주세요.",
  attachment_already_sent: "이미 보낸 파일이에요.",
  bad_attachment_path: "파일을 올리지 못했어요. 다시 시도해 주세요.",
  bad_attachments: "파일을 올리지 못했어요. 다시 시도해 주세요.",
  unknown: "처리하지 못했어요. 잠시 뒤 다시 시도해 주세요.",
};

/* ───────────── 서버 함수 이름·인자 (호출 모양 테스트가 마이그레이션과 대조) ───────────── */

export const RPC = Object.freeze({
  myState: "my_ops_chat_state",
  myRooms: "my_ops_chat_rooms",
  thread: "ops_chat_thread",
  send: "ops_chat_send",
  markRead: "ops_chat_mark_read",
  adminOpen: "admin_ops_chat_open",
  adminClose: "admin_ops_chat_close",
  adminAssign: "admin_ops_chat_assign",
  adminList: "admin_ops_chat_list",
  adminMemberSearch: "admin_ops_chat_member_search",
  adminBadge: "admin_inquiry_menu_badge",
  // 2026-10-28 메신저 P1 (마이그레이션 20261207100000 · 20261207100100)
  guideOn: "ops_chat_guide_on",
  viewing: "ops_chat_viewing",
  setMuted: "ops_chat_set_muted",
  roomMuted: "ops_chat_room_muted",
  myTopic: "my_ops_chat_topic",
  notifyPref: "my_chat_notify_pref",
  setNotifyPref: "set_my_chat_notify_pref",
  adminTopics: "admin_support_topic_list",
  adminTopicSave: "admin_support_topic_save",
  adminKbTopics: "admin_kb_entry_set_topics",
  adminHolidays: "admin_ops_holiday_list",
  adminHolidaySet: "admin_ops_holiday_set",
  adminPathStats: "admin_support_path_stats",
});

async function rpc(name, args) {
  const { data, error } = await supabase.rpc(name, args);
  if (error) throw error;
  return data;
}

export const fetchMyState = () => rpc(RPC.myState);
export const fetchMyRooms = async () => (await rpc(RPC.myRooms)) || [];
export const fetchThread = (roomId) => rpc(RPC.thread, { p_room_id: roomId });
export const markRead = (roomId) => rpc(RPC.markRead, { p_room_id: roomId });

/** 파일을 올리고(실패하면 throw) 서버에 넘길 첨부 목록을 돌려준다 */
export async function uploadAttachments(roomId, files) {
  const list = Array.from(files || []);
  if (list.length > MAX_FILES_PER_MESSAGE) throw new Error("too_many_attachments");
  for (const f of list) {
    const bad = validateFile(f);
    if (bad) throw new Error(bad);
  }
  const out = [];
  for (const f of list) {
    const path = buildAttachmentPath(roomId, f.name);
    const { error } = await supabase.storage.from(OPS_CHAT_BUCKET).upload(path, f, { contentType: contentTypeOf(f), upsert: false });
    if (error) throw error;
    out.push({ path, file_name: f.name });
  }
  return out;
}

/** 메시지 보내기 — 파일이 있으면 먼저 올린다 */
export async function sendMessage(roomId, body, files = []) {
  const attachments = await uploadAttachments(roomId, files);
  return rpc(RPC.send, { p_room_id: roomId, p_body: body || "", p_attachments: attachments });
}

export async function signedUrlOf(path, { download } = {}) {
  const { data, error } = await supabase.storage.from(OPS_CHAT_BUCKET)
    .createSignedUrl(path, SIGNED_URL_TTL_SEC, download ? { download } : undefined);
  if (error) throw error;
  return data?.signedUrl || null;
}

/* ───────────── 운영팀 ───────────── */

export const adminListRooms = async ({ status = "open", mine = false, q = "" } = {}) =>
  (await rpc(RPC.adminList, { p_status: status, p_mine: mine, p_q: q || null, p_limit: 200, p_offset: 0 })) || [];
export const adminOpenRoom = (creatorEmail, body, sourceInquiryId = null) =>
  rpc(RPC.adminOpen, { p_creator_email: creatorEmail, p_body: body, p_source_inquiry_id: sourceInquiryId });
export const adminCloseRoom = (roomId, reason, lastMessage, note) =>
  rpc(RPC.adminClose, { p_room_id: roomId, p_reason: reason, p_last_message: lastMessage || null, p_note: note || null });
export const adminAssignRoom = (roomId, assigneeEmail) => rpc(RPC.adminAssign, { p_room_id: roomId, p_assignee_email: assigneeEmail });
export const adminSearchMembers = async (q) => (await rpc(RPC.adminMemberSearch, { p_q: q })) || [];
export const adminMenuBadge = async () => Number(await rpc(RPC.adminBadge)) || 0;

/* ───────────── 화면 도우미 ───────────── */

/** 마지막 메시지 미리보기 — 첨부만 있으면 「사진 1장 · 파일 1개」 같은 말 대신 개수만 (어드민 목록용) */
export function adminPreviewText(row) {
  const body = String(row?.last_preview || "").trim();
  const n = Number(row?.last_attachment_count) || 0;
  if (body && n) return `${body} · 첨부 ${n}개`;
  if (body) return body;
  if (n) return `첨부 ${n}개`;
  return "";
}

/** 어드민 목록·대화 머리의 연결 이유 */
export const OPS_ORIGIN_LABEL = { lay: "레이에서 연결", lay_down: "레이 장애로 연결", urgent: "급한 문제" };
/** 어드민 빠른 답장 (대화방 관리 · 떠 있는 대화창 공용) */
export const ADMIN_QUICK_REPLIES = ["확인했어요. 감사합니다.", "잠시만 기다려 주세요. 확인하고 다시 말씀드릴게요."];

/** 목록 행의 회원 이름 */
export const adminRoomName = (row) => row?.creator_nick || row?.creator_name || row?.creator_email || "";

/** 떠 있는 대화창 버튼 숫자 — 열린 방들의 안 읽은 크리에이터 메시지 합 */
export function adminUnreadTotal(rows) {
  return (rows || []).reduce((s, r) => s + (r?.status === "open" ? Math.max(0, Number(r.unread) || 0) : 0), 0);
}

/** 새 답장 말풍선에 보일 방 — 안 읽은 메시지가 있는 열린 방 중 가장 최근 */
export function latestUnreadRow(rows) {
  let best = null;
  for (const r of rows || []) {
    if (r?.status !== "open" || !(Number(r.unread) > 0)) continue;
    if (!best || String(r.last_message_at || "") > String(best.last_message_at || "")) best = r;
  }
  return best;
}

/** 「대화방 관리」 화면에서는 떠 있는 대화창을 숨긴다(같은 대화가 두 번 보이지 않게) */
export function isAdminOpsChatPath(pathname) {
  return /^\/admin\/inquiries\/chats(\/|$)/.test(String(pathname || ""));
}

export const CLOSE_REASONS = [
  { value: "resolved", label: "해결됐어요" },
  { value: "no_reply", label: "크리에이터가 답이 없어요" },
  { value: "moved_to_inquiry", label: "문의하기로 안내했어요" },
];

/* ───────────── 메신저 P1 — 실시간 · 보고 있는 중 · 방 알림 · 알림 설정 (2026-10-28) ───────────── */

/** 화면이 방을 열어 둔 동안 30초마다 — 서버는 45초 동안 그 방 푸시를 건너뛴다 */
export const VIEWING_PING_MS = 30 * 1000;
/** Realtime 이 끊겼을 때 대신 다시 읽는 간격 */
export const FALLBACK_POLL_MS = 30 * 1000;

export const fetchGuideOn = async () => Boolean(await rpc(RPC.guideOn));
export const setViewing = (roomId, on = true) => rpc(RPC.viewing, { p_room_id: roomId, p_on: on });
export const setRoomMuted = (roomId, muted) => rpc(RPC.setMuted, { p_room_id: roomId, p_muted: muted });
export const fetchRoomMuted = async (roomId) => Boolean(await rpc(RPC.roomMuted, { p_room_id: roomId }));
export const fetchMyTopic = () => rpc(RPC.myTopic);
export const fetchNotifyPref = () => rpc(RPC.notifyPref);
export const saveNotifyPref = ({ opsChatPush = null, showPreview = null } = {}) =>
  rpc(RPC.setNotifyPref, { p_ops_chat_push: opsChatPush, p_show_preview: showPreview });

export const adminListTopics = async () => (await rpc(RPC.adminTopics)) || [];
export const adminSaveTopic = (t) => rpc(RPC.adminTopicSave, {
  p_key: t.key, p_parent_key: t.parent_key || null, p_label: t.label, p_icon: t.icon || null, p_sort: Number(t.sort) || 100,
  p_guide_slugs: t.guide_slugs || [], p_is_urgent: Boolean(t.is_urgent), p_active: t.active !== false,
});
export const adminSetKbTopics = (entryId, topicKeys) => rpc(RPC.adminKbTopics, { p_entry_id: entryId, p_topic_keys: topicKeys || [] });
export const adminListHolidays = async (from) => (await rpc(RPC.adminHolidays, from ? { p_from: from } : {})) || [];
export const adminSetHoliday = (day, name, on = true) => rpc(RPC.adminHolidaySet, { p_day: day, p_name: name || "", p_on: on });
export const adminPathStats = async (days = 30) => (await rpc(RPC.adminPathStats, { p_days: days })) || [];

/**
 * Realtime 비공개 채널 구독 — 신호만 온다(본문 없음). 신호가 오면 onSignal(event, payload) 에서 RPC 로 다시 읽는다.
 * 돌려준 함수를 부르면 구독을 끊는다. 실패해도 화면은 FALLBACK_POLL_MS 다시 읽기로 돈다.
 */
export function subscribeOpsChat(topic, onSignal) {
  if (!topic || typeof supabase?.channel !== "function") return () => {};
  let channel = null;
  try {
    channel = supabase.channel(topic, { config: { private: true } })
      .on("broadcast", { event: "*" }, (msg) => onSignal?.(msg?.event, msg?.payload || {}))
      .subscribe();
  } catch {
    return () => {};
  }
  return () => { try { supabase.removeChannel(channel); } catch { /* 이미 끊김 */ } };
}

/** 다른 화면(문의하기 등)에서 연결된 방을 대화창으로 열 때 — OpsChatLauncher 가 듣는다 */
export const OPS_CHAT_OPEN_EVENT = "laeebly:ops-chat:open";
export function openOpsChatRoom(roomId) {
  if (typeof window === "undefined") return;
  try { window.dispatchEvent(new CustomEvent(OPS_CHAT_OPEN_EVENT, { detail: { roomId: roomId || null } })); } catch { /* 무시 */ }
}

